import { useEffect, useState, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Beaker, RefreshCw, ChevronDown, ChevronUp,
  Search, X, CheckCircle2, Clock, AlertCircle, AlertTriangle,
  Wifi, WifiOff, Link2, Rocket, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_CONFIG = {
  ready_for_market: {
    label: "Ready for Market",
    color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    dot: "bg-emerald-400",
  },
  in_production: {
    label: "En Producción",
    color: "bg-blue-500/15 text-blue-400 border-blue-500/30",
    dot: "bg-blue-400",
  },
};

function authHeaders() {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  return {
    Authorization: `Bearer ${token}`,
    "X-Api-Key": "valoriza-innovations-api-2026",
  };
}

export default function AppCatalogPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [health, setHealth] = useState(null);
  const [query, setQuery] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [meta, setMeta] = useState(null);
  const [cacheStatus, setCacheStatus] = useState(null);

  // Admin: link modal + transition busy state
  const [linkTarget, setLinkTarget] = useState(null); // {id, name}
  const [candidates, setCandidates] = useState(null); // {projects, initiatives}
  const [busyId, setBusyId] = useState(null);

  const fetchItems = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/innovations/ready-for-market`, {
        headers: authHeaders(),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setItems(data.innovations || []);
      setCacheStatus(forceRefresh ? "MISS" : "HIT");
      setMeta({ count: data.count, generated_at: data.generated_at });
      setHealth("ok");
    } catch (e) {
      setError(String(e.message || e));
      setHealth("error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openLinkModal = useCallback(async (item) => {
    setLinkTarget({ id: item.id, name: item.name, initiative_id: item.initiative_id });
    if (candidates) return;
    try {
      const r = await fetch(`${BACKEND_URL}/api/innovations/link-candidates`, { headers: authHeaders() });
      if (r.ok) setCandidates(await r.json());
      else toast.error("No pude cargar los proyectos disponibles");
    } catch (e) {
      toast.error(`Error cargando proyectos: ${e.message}`);
    }
  }, [candidates]);

  const doLink = useCallback(async (initiativeId) => {
    if (!linkTarget) return;
    setBusyId(linkTarget.id);
    try {
      const r = await fetch(`${BACKEND_URL}/api/innovations/${linkTarget.id}/link`, {
        method: "PATCH",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ initiative_id: initiativeId }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      toast.success(`Vinculado a ${data.linked_project?.name || data.initiative_name}`);
      setLinkTarget(null);
      fetchItems(true);
    } catch (e) {
      toast.error(`Error vinculando: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  }, [linkTarget, fetchItems]);

  const doTransition = useCallback(async (item, targetStage) => {
    setBusyId(item.id);
    try {
      const r = await fetch(`${BACKEND_URL}/api/innovations/${item.id}/transition`, {
        method: "PATCH",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ stage: targetStage }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      toast.success(`«${item.name}» ahora está ${STAGE_CONFIG[targetStage]?.label || targetStage}`);
      fetchItems(true);
    } catch (e) {
      toast.error(`Error moviendo estado: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  }, [fetchItems]);

  const orphanCount = items.filter((item) => !item.portfolio_project).length;

  const filtered = items.filter((item) => {
    const matchStage = stageFilter === "all" || (stageFilter === "orphans" ? !item.portfolio_project : item.stage === stageFilter);
    const q = query.trim().toLowerCase();
    const matchQ = !q || [item.name, item.description, item.partner, item.hypothesis]
      .join(" ").toLowerCase().includes(q);
    return matchStage && matchQ;
  });

  return (
    <div className="max-w-6xl mx-auto px-6 py-8 space-y-6" data-testid="app-catalog-page">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Beaker className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            <span className="text-emerald-600 dark:text-emerald-400 text-xs uppercase tracking-widest font-bold">
              InnovationOS · Valoriza
            </span>
            {health === "ok" && (
              <span className="flex items-center gap-1 text-[10px] text-emerald-600/70 dark:text-emerald-400/70">
                <Wifi className="w-3 h-3" /> conectado
              </span>
            )}
            {health === "error" && (
              <span className="flex items-center gap-1 text-[10px] text-red-500/80">
                <WifiOff className="w-3 h-3" /> sin conexión
              </span>
            )}
          </div>
          <h1 className="text-3xl font-black tracking-tight">Catálogo de Innovaciones</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Soluciones validadas en campo, listas para incorporar en nuevas ofertas
          </p>
        </div>
        <div className="flex items-center gap-2">
          {meta && (
            <div className="text-right hidden sm:block">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">
                {cacheStatus === "HIT" ? "Caché · " : "Datos frescos · "}
                {meta.count} soluciones
              </div>
              <div className="text-[10px] text-muted-foreground/60">
                {meta.generated_at && new Date(meta.generated_at).toLocaleString("es-ES")}
              </div>
            </div>
          )}
          <Button
            size="sm" variant="outline"
            onClick={() => fetchItems(true)} disabled={loading}
            data-testid="catalog-refresh-btn" className="gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Actualizar
          </Button>
        </div>
      </div>

      {/* ── Filters ────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-48 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por nombre, tecnología, partner..."
            data-testid="catalog-search" className="pl-9 h-9 rounded-xl"
          />
          {query && (
            <button onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2">
              <X className="w-3.5 h-3.5 text-muted-foreground hover:text-foreground" />
            </button>
          )}
        </div>

        {["all", "ready_for_market", "in_production", "orphans"].map((s) => (
          <button
            key={s} data-testid={`catalog-filter-${s}`}
            onClick={() => setStageFilter(s)}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-all ${
              stageFilter === s
                ? "bg-emerald-600 text-white border-emerald-600"
                : s === "orphans" && orphanCount > 0
                  ? "border-orange-400 text-orange-600 hover:bg-orange-50 bg-background"
                  : "border-border text-muted-foreground hover:border-emerald-500 hover:text-foreground bg-background"
            }`}
          >
            {s === "all" ? "Todos" : s === "orphans" ? `Huérfanas${orphanCount ? ` (${orphanCount})` : ""}` : STAGE_CONFIG[s]?.label || s}
          </button>
        ))}

        {filtered.length !== items.length && (
          <span className="text-xs text-muted-foreground">{filtered.length} de {items.length}</span>
        )}
      </div>

      {/* ── Error ──────────────────────────────────────────────────────── */}
      {error && (
        <div className="rounded-2xl border border-red-500/20 bg-red-50 dark:bg-red-500/10 p-5 flex items-start gap-3" data-testid="catalog-error">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-red-700 dark:text-red-300 font-semibold text-sm">Error conectando con InnovationOS</div>
            <div className="text-red-600/70 dark:text-red-300/70 text-xs mt-1">{error}</div>
            <Button size="sm" variant="outline" onClick={() => fetchItems(true)} className="mt-3 text-xs border-red-300 text-red-600 hover:bg-red-50">
              Reintentar
            </Button>
          </div>
        </div>
      )}

      {/* ── Loading ────────────────────────────────────────────────────── */}
      {loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-2xl border bg-card p-5 animate-pulse">
              <div className="h-4 bg-muted rounded w-3/4 mb-3" />
              <div className="h-3 bg-muted/60 rounded w-full mb-2" />
              <div className="h-3 bg-muted/60 rounded w-5/6" />
            </div>
          ))}
        </div>
      )}

      {/* ── Cards ──────────────────────────────────────────────────────── */}
      {!loading && !error && (
        <>
          {filtered.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground" data-testid="catalog-empty">
              <Beaker className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">
                {stageFilter === "orphans"
                  ? "No hay fichas huérfanas: todo el catálogo está vinculado a Portfolio."
                  : `Sin soluciones${query ? ` para «${query}»` : ""}`}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <AnimatePresence>
                {filtered.map((item, i) => (
                  <InnovationCard
                    key={item.id}
                    item={item}
                    index={i}
                    expanded={expandedId === item.id}
                    onToggle={() => setExpandedId(expandedId === item.id ? null : item.id)}
                    onOpenLink={() => openLinkModal(item)}
                    onTransition={(s) => doTransition(item, s)}
                    busy={busyId === item.id}
                  />
                ))}
              </AnimatePresence>
            </div>
          )}
        </>
      )}

      {/* ── Link Modal ─────────────────────────────────────────────────── */}
      <AnimatePresence>
        {linkTarget && (
          <LinkPortfolioModal
            target={linkTarget}
            candidates={candidates}
            onClose={() => setLinkTarget(null)}
            onLink={doLink}
            busy={busyId === linkTarget.id}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── InnovationCard ───────────────────────────────────────────────────────────

function InnovationCard({ item, index, expanded, onToggle, onOpenLink, onTransition, busy }) {
  const stage = STAGE_CONFIG[item.stage] || STAGE_CONFIG.ready_for_market;
  const isReadyForMarket = item.stage === "ready_for_market";
  const isInProduction = item.stage === "in_production";
  const missingLink = !item.portfolio_project;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ delay: index * 0.04 }}
      className="rounded-2xl border bg-card hover:bg-accent/30 transition-colors overflow-hidden"
      data-testid={`catalog-card-${item.id}`}
    >
      <div className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${stage.color}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${stage.dot}`} />
                {stage.label}
              </span>
              {item.partner && (
                <span className="text-[10px] text-muted-foreground font-medium">{item.partner}</span>
              )}
            </div>
            <h3 className="text-base font-bold leading-tight">{item.name}</h3>
          </div>
        </div>

        <p className="text-muted-foreground text-sm leading-relaxed line-clamp-2">
          {item.description || item.hypothesis || "Sin descripción."}
        </p>

        <div className="flex flex-wrap gap-3 mt-4">
          {item.cost_estimated && <Chip label="Coste" value={`${Number(item.cost_estimated).toLocaleString("es-ES")} €`} />}
          {item.duration_weeks && <Chip label="Duración" value={`${item.duration_weeks} sem`} />}
          {item.portfolio_project && (
            <Chip label={item.portfolio_project.is_initiative_only ? "Iniciativa" : "Portfolio"} value={item.portfolio_project.name} accent />
          )}
        </div>

        {/* ── Admin quick-actions bar ─────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-border/60">
          {missingLink && (
            <Button
              size="sm" variant="outline"
              onClick={onOpenLink}
              disabled={busy}
              className="gap-1.5 border-amber-400 text-amber-700 hover:bg-amber-50 dark:text-amber-300 dark:hover:bg-amber-500/10 h-7 text-xs"
              data-testid={`catalog-link-btn-${item.id}`}
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Link2 className="w-3 h-3" />}
              Vincular a Portfolio
            </Button>
          )}
          {isReadyForMarket && (
            <Button
              size="sm" variant="outline"
              onClick={() => onTransition("in_production")}
              disabled={busy}
              className="gap-1.5 border-blue-400 text-blue-700 hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-blue-500/10 h-7 text-xs"
              data-testid={`catalog-to-production-btn-${item.id}`}
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Rocket className="w-3 h-3" />}
              Pasar a Producción
            </Button>
          )}
          {isInProduction && (
            <Button
              size="sm" variant="ghost"
              onClick={() => onTransition("ready_for_market")}
              disabled={busy}
              className="gap-1.5 text-emerald-700 hover:bg-emerald-50 dark:text-emerald-300 dark:hover:bg-emerald-500/10 h-7 text-xs"
              data-testid={`catalog-to-market-btn-${item.id}`}
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Beaker className="w-3 h-3" />}
              Volver a Ready for Market
            </Button>
          )}
        </div>
      </div>

      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-2.5 bg-muted/30 hover:bg-muted/50 border-t border-border text-xs text-muted-foreground hover:text-foreground transition-colors"
        data-testid={`catalog-expand-${item.id}`}
      >
        <span>{expanded ? "Ocultar detalle" : "Ver ficha completa"}</span>
        {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="overflow-hidden"
          >
            <div className="px-5 py-4 space-y-4 border-t border-border" data-testid={`catalog-detail-${item.id}`}>
              {item.description && (
                <DetailRow label="Descripción completa" value={item.description} testid={`catalog-full-description-${item.id}`} />
              )}
              {item.hypothesis && item.hypothesis !== item.description && (
                <DetailRow label="Hipótesis" value={item.hypothesis} testid={`catalog-full-hypothesis-${item.id}`} />
              )}
              {item.decision_notes && (
                <DetailRow label="Notas de decisión" value={item.decision_notes} testid={`catalog-decision-notes-${item.id}`} />
              )}

              {(Object.keys(item.kpi_targets || {}).length > 0 || Object.keys(item.kpi_actuals || {}).length > 0) && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">KPIs</div>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(item.kpi_targets || {}).map(([k, v]) => (
                      <div key={k} className="rounded-lg bg-muted/40 px-3 py-2">
                        <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{k} · Objetivo</div>
                        <div className="text-sm font-bold">{String(v)}</div>
                        {item.kpi_actuals?.[k] && (
                          <div className="text-xs text-emerald-400 mt-0.5">
                            <CheckCircle2 className="w-3 h-3 inline mr-1" />
                            Real: {String(item.kpi_actuals[k])}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {item.technologies?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Tecnologías</div>
                  <div className="flex flex-wrap gap-1.5">
                    {item.technologies.map((t) => (
                      <span key={t} className="text-xs px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-300">{t}</span>
                    ))}
                  </div>
                </div>
              )}

              {item.portfolio_project ? (
                <div className="rounded-xl bg-amber-500/10 border border-amber-500/20 px-4 py-3" data-testid={`catalog-portfolio-link-${item.id}`}>
                  <div className="text-[10px] uppercase tracking-wider text-amber-800 dark:text-amber-300 font-semibold mb-1">
                    {item.portfolio_project.is_initiative_only ? "Iniciativa PG-07 vinculada (sin proyecto Portfolio aún)" : "Proyecto Portfolio vinculado"}
                  </div>
                  <div className="text-sm font-bold text-amber-700 dark:text-amber-200">{item.portfolio_project.name}</div>
                  {item.portfolio_project.description && (
                    <div className="text-xs text-amber-700/70 dark:text-amber-100/60 mt-1 whitespace-pre-wrap">{item.portfolio_project.description}</div>
                  )}
                  <div className="flex gap-3 mt-2 text-xs text-amber-600/60 dark:text-amber-100/50 flex-wrap">
                    <span>Estado: {item.portfolio_project.status}</span>
                    {item.portfolio_project.project_manager && <span>PM: {item.portfolio_project.project_manager}</span>}
                    {item.portfolio_project.budget_total && (
                      <span>Presupuesto: {Number(item.portfolio_project.budget_total).toLocaleString("es-ES")} €</span>
                    )}
                  </div>
                  <div className="mt-2">
                    <button
                      onClick={onOpenLink}
                      className="text-[10px] font-semibold text-amber-700 dark:text-amber-200 underline decoration-dotted hover:text-amber-900"
                      data-testid={`catalog-relink-btn-${item.id}`}
                    >
                      Cambiar vínculo
                    </button>
                  </div>
                </div>
              ) : (
                <div className="rounded-xl bg-orange-500/10 border border-dashed border-orange-500/40 px-4 py-3 flex flex-col sm:flex-row items-start sm:items-center gap-3 text-sm text-orange-700 dark:text-orange-300" data-testid={`catalog-missing-link-${item.id}`}>
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  <div className="flex-1">
                    <strong>Sin vínculo a Portfolio / Piloto.</strong>
                    <span className="ml-1">Toda innovación en el catálogo debe estar vinculada a un proyecto real.</span>
                  </div>
                  <Button
                    size="sm"
                    onClick={onOpenLink}
                    disabled={busy}
                    className="gap-1.5 bg-orange-600 hover:bg-orange-700 text-white h-7 text-xs"
                    data-testid={`catalog-link-inline-btn-${item.id}`}
                  >
                    <Link2 className="w-3 h-3" /> Vincular ahora
                  </Button>
                </div>
              )}

              {(item.start_date || item.end_date) && (
                <div className="flex gap-4 text-xs text-muted-foreground">
                  {item.start_date && <span><Clock className="w-3 h-3 inline mr-1" />Inicio: {item.start_date}</span>}
                  {item.end_date && <span>Fin: {item.end_date}</span>}
                </div>
              )}

              <div className="text-[9px] text-muted-foreground/40 pt-1">
                ID: {item.id} · source: {item.source}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ─── LinkPortfolioModal ───────────────────────────────────────────────────────

function LinkPortfolioModal({ target, candidates, onClose, onLink, busy }) {
  const [q, setQ] = useState("");
  const [mode, setMode] = useState("projects"); // "projects" | "initiatives"

  const list = useMemo(() => {
    if (!candidates) return [];
    const src = mode === "projects" ? candidates.projects : candidates.initiatives;
    const query = q.trim().toLowerCase();
    if (!query) return src;
    return src.filter((x) => (x.name || "").toLowerCase().includes(query));
  }, [candidates, mode, q]);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="catalog-link-modal"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
        className="w-full max-w-lg bg-card rounded-2xl border border-border shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-border flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-emerald-600 font-bold">Admin · Catálogo</div>
            <div className="text-base font-bold truncate">Vincular «{target.name}»</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Elige una iniciativa PG-07 o un proyecto de portfolio para dejar de aparecer como huérfana.
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground flex-shrink-0"
            data-testid="catalog-link-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-3 flex gap-2">
          {[
            { key: "projects", label: "Proyectos Portfolio" },
            { key: "initiatives", label: "Iniciativas (PG-07)" },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setMode(t.key)}
              className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${
                mode === t.key
                  ? "bg-emerald-600 text-white border-emerald-600"
                  : "bg-transparent border-border text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`catalog-link-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="px-5">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={`Filtrar ${mode === "projects" ? "proyectos" : "iniciativas"}…`}
              data-testid="catalog-link-modal-search"
              className="pl-9 h-9 rounded-lg"
            />
          </div>
        </div>

        <div className="px-5 py-3 max-h-[45vh] overflow-y-auto space-y-2" data-testid="catalog-link-modal-list">
          {!candidates && (
            <div className="text-xs text-muted-foreground py-4 text-center">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Cargando proyectos disponibles…
            </div>
          )}
          {candidates && list.length === 0 && (
            <div className="text-xs text-muted-foreground py-4 text-center">Sin resultados.</div>
          )}
          {candidates && list.map((it) => (
            <div
              key={it.id}
              className="rounded-lg border border-border p-3 flex items-center justify-between gap-3 hover:bg-muted/40 transition-colors"
              data-testid={`catalog-link-candidate-${it.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold truncate">{it.name}</div>
                <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
                  {mode === "projects" ? (
                    <>Portfolio · {it.status || "—"} {it.project_manager ? `· ${it.project_manager}` : ""}</>
                  ) : (
                    <>{it.type || "—"} · {it.status || "—"}</>
                  )}
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => onLink(mode === "projects" ? it.initiative_id : it.id)}
                disabled={busy || (mode === "projects" && !it.initiative_id)}
                className="h-7 text-xs gap-1"
                data-testid={`catalog-link-select-${it.id}`}
              >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Link2 className="w-3 h-3" />}
                Vincular
              </Button>
            </div>
          ))}
        </div>

        <div className="px-5 py-3 border-t border-border text-[10px] text-muted-foreground">
          Vincular a un proyecto de Portfolio o a una iniciativa PG-07 es suficiente para que la ficha deje de aparecer como huérfana.
        </div>
      </motion.div>
    </motion.div>
  );
}

function Chip({ label, value, accent }) {
  return (
    <div className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs ${
      accent ? "bg-amber-500/10 border border-amber-500/20 text-amber-700 dark:text-amber-200" : "bg-muted/40 border border-border text-muted-foreground"
    }`}>
      <span className="text-[9px] uppercase tracking-wide opacity-60">{label}</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}

function DetailRow({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{label}</div>
      <p className="text-sm text-foreground/85 leading-relaxed whitespace-pre-wrap">{value}</p>
    </div>
  );
}
